import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { fetchIndustries } from "../store/industrySlice";
import logo from "../assets/images/RW_Logo_SVG.svg";
import {
  FaChevronDown,
  FaBars,
  FaTimes,
  FaSearch,
  FaThLarge,
  FaSignOutAlt,
} from "react-icons/fa";
import SearchBar from "./SearchBar";
import { useAuth } from "../context/AuthContext";

import { getIndustryFaIcon } from "../utils/iconHelper";
import { servicesData } from "../data/servicesData";
import { slugifyIndustry, STATIC_INDUSTRIES_MAP } from "../utils/industryData";
import { DETAILED_INDUSTRY_CONTENT } from "../utils/detailedIndustryContent";

const Header = () => {
  const { endUser, saveSearchReport, logoutEndUser } = useAuth() || {};
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    if (logoutEndUser) {
      logoutEndUser();
    }
    navigate("/", { replace: true });
  };
  const dispatch = useDispatch();
  const { reports: baseReports } = useSelector(
    (state) => state.researchReports || { reports: [] },
  );
  const { hierarchy: industryHierarchy, fetched: industriesFetched } =
    useSelector(
      (state) => state.industries || { hierarchy: {}, fetched: false },
    );

  useEffect(() => {
    if (!industriesFetched) {
      dispatch(fetchIndustries());
    }
  }, [dispatch, industriesFetched]);

  const dynamicHierarchy = useMemo(() => {
    // 1. Initialize canonical hierarchy with exactly the 17 official industries
    const canonicalKeys = Object.keys(STATIC_INDUSTRIES_MAP);
    const hierarchy = {};

    canonicalKeys.forEach((ind) => {
      const key = slugifyIndustry(ind);
      const subs = industryHierarchy?.[ind] || [];
      hierarchy[key] = {
        id: "dyn-" + key,
        name: ind,
        icon: null,
        subCategories: new Set(subs),
        reportCount: 0,
      };
    });

    // Helper to resolve an arbitrary industry string to one of the 17 canonical keys
    const normalizeToCanonical = (raw) => {
      if (!raw || typeof raw !== "string") return null;
      const clean = raw.trim().replace(/\s+/g, " ");
      const slug = slugifyIndustry(clean);
      if (hierarchy[slug]) return slug;

      // Handle common aliases or loose variations
      const aliasMap = {
        energy: slugifyIndustry("Energy & Utilities"),
        "energy-utilities": slugifyIndustry("Energy & Utilities"),
        "chemicals-and-materials": slugifyIndustry("Chemicals & Materials"),
        bfsi: slugifyIndustry(
          "Banking, Financial Services, and Insurance (BFSI)",
        ),
        "banking-financial-services-and-insurance": slugifyIndustry(
          "Banking, Financial Services, and Insurance (BFSI)",
        ),
        ict: slugifyIndustry(
          "Information & Communication Technology (ICT)",
        ),
        "information-communication-technology": slugifyIndustry(
          "Information & Communication Technology (ICT)",
        ),
        logistics: slugifyIndustry(
          "Logistics, Transportation & Supply Chain",
        ),
        "logistics-transportation-and-supply-chain": slugifyIndustry(
          "Logistics, Transportation & Supply Chain",
        ),
        "construction-and-infrastructure": slugifyIndustry(
          "Construction & Infrastructure",
        ),
        "manufacturing-and-industrial": slugifyIndustry(
          "Manufacturing & Industrial",
        ),
        "consumer-goods-retail": slugifyIndustry(
          "Consumer Goods and Retail",
        ),
        "aerospace-defense": slugifyIndustry("Aerospace and Defense"),
      };

      return aliasMap[slug] || null;
    };

    if (baseReports && baseReports.length > 0) {
      const publishedReports = baseReports.filter(
        (r) => !r.status || r.status.toLowerCase() === "published",
      );
      publishedReports.forEach((r) => {
        const inds = [r.category, r.industry, r.Industry]
          .filter(Boolean)
          .map((x) => (typeof x === "string" ? x.trim() : x));
        const subs = [r.subCategory, r.sub_category, r.sub_industry]
          .filter(Boolean)
          .map((x) => (typeof x === "string" ? x.trim() : x));

        inds.forEach((rawInd) => {
          const canonicalKey = normalizeToCanonical(rawInd);
          if (canonicalKey && hierarchy[canonicalKey]) {
            hierarchy[canonicalKey].reportCount += 1;
            subs.forEach((sub) => {
              if (sub.toLowerCase() !== "general") {
                hierarchy[canonicalKey].subCategories.add(sub);
              }
            });
          }
        });
      });
    }

    return Object.values(hierarchy)
      .map((c) => ({
        ...c,
        subCategories: Array.from(c.subCategories).sort(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [baseReports, industryHierarchy]);

  const renderIndustryItem = (cat) => {
    const industrySlug = slugifyIndustry(cat.name);
    const predefinedAreas =
      DETAILED_INDUSTRY_CONTENT[industrySlug]?.coverage?.areas ||
      cat.subCategories;

    let displayItems = [];
    let charCount = 0;
    const MAX_CHARS = 85; // Target length to comfortably fill 2 lines without exceeding, leaving room for 'and more'

    for (let i = 0; i < predefinedAreas.length; i++) {
      if (
        charCount + predefinedAreas[i].length > MAX_CHARS &&
        displayItems.length > 0
      ) {
        break;
      }
      displayItems.push(predefinedAreas[i]);
      charCount += predefinedAreas[i].length + 2; // +2 for comma and space
    }

    const hasMore = displayItems.length < predefinedAreas.length;

    return (
      <div className="col-4 d-flex" key={cat.id}>
        <Link
          to={`/industries/${industrySlug}`}
          onClick={() => setIsIndustriesOpen(false)}
          className="d-flex align-items-start gap-2 text-decoration-none text-dark hover-bg-light p-1 rounded-3 transition-all w-100"
          title={`Browse ${cat.name}`}
        >
          {(() => {
            const IconComponent = getIndustryFaIcon(cat.name);
            return (
              <IconComponent
                style={{
                  fontSize: "18px",
                  color: "#053b84",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
            );
          })()}
          <div>
            <div
              className="fw-bold mb-0 text-nowrap"
              style={{ fontSize: "0.8rem" }}
            >
              {cat.name}
            </div>
            <div
              className="text-muted pe-2"
              style={{
                fontSize: "0.7rem",
                lineHeight: "1.2",
                minHeight: "2.4em", // Force exact 2-line height for equal spacing
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {displayItems.join(", ")}
              {hasMore && (
                <>
                  {displayItems.length > 0 ? ", " : ""}
                  <span className="fw-bold" style={{ color: "#053b84" }}>
                    and more
                  </span>
                </>
              )}
            </div>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <header
      className="bg-white border-bottom sticky-top header-container"
      style={{ zIndex: 1040 }}
    >
      <div
        className="container-fluid px-4 px-md-3 d-flex justify-content-between align-items-center"
        style={{ height: "72px" }}
      >
        {/* Left: Logo */}
        <a
          className="navbar-brand d-flex align-items-center text-decoration-none"
          href="/"
          aria-label="Researchwire Home"
          style={{ outline: "none" }}
        >
          <img
            src={logo}
            alt="Researchwire Logo"
            className="header-logo"
            width="140"
            height="65"
            style={{ objectFit: "contain", outline: "none" }}
          />
        </a>

        {/* Right: Actions and Navigation (Desktop) */}
        <div className="d-none d-md-flex align-items-center gap-2 gap-lg-4 position-relative">
          <nav className="d-flex align-items-center gap-2 gap-md-3 me-2 header-nav-group">
            <a
              href="/reports"
              className="text-decoration-none text-dark fw-bold small text-nowrap link-hover header-nav-link"
            >
              All Reports
            </a>

            <div
              className="nav-item-industries position-relative h-100 d-flex align-items-center"
              onMouseEnter={() => setIsIndustriesOpen(true)}
              onMouseLeave={() => setIsIndustriesOpen(false)}
              style={{ cursor: "pointer", padding: "24px 0" }}
            >
              <Link
                to="/industries"
                className="text-decoration-none text-dark fw-bold small text-nowrap d-flex align-items-center gap-1 link-hover header-nav-link border-0 bg-transparent"
                aria-expanded={isIndustriesOpen}
                aria-haspopup="true"
              >
                Industries{" "}
                <FaChevronDown
                  size={10}
                  className={`transition-transform ${isIndustriesOpen ? "rotate-180" : ""}`}
                />
              </Link>

              {/* Dropdown Content */}
              {isIndustriesOpen && (
                <div
                  className="mega-menu-dropdown position-absolute bg-white shadow-lg rounded-4 p-3 mega-menu-fade-in"
                  style={{
                    top: "70px",
                    left: "180%",
                    transform: "translateX(-50%)",
                    width: "1150px",
                    cursor: "default",
                    border: "1px solid #f0f0f0",
                    zIndex: 950,
                  }}
                >
                  <h6
                    className="fw-bold mb-3 small letter-spacing-1 text-uppercase"
                    style={{ color: "#97A3B7" }}
                  >
                    Industries
                  </h6>
                  <div className="row gx-4 gy-2">
                    {dynamicHierarchy.map(renderIndustryItem)}
                  </div>
                </div>
              )}
            </div>

            {/* Services Menu Dropdown */}
            <div
              className="nav-item-services position-relative h-100 d-flex align-items-center"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
              style={{ cursor: "pointer", padding: "24px 0" }}
            >
              <Link
                to="/services"
                className="text-decoration-none text-dark fw-bold small text-nowrap d-flex align-items-center gap-1 link-hover header-nav-link border-0 bg-transparent"
                aria-expanded={isServicesOpen}
                aria-haspopup="true"
              >
                Services{" "}
                <FaChevronDown
                  size={10}
                  className={`transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
                />
              </Link>

              {/* Dropdown Content */}
              {isServicesOpen && (
                <div
                  className="mega-menu-dropdown position-absolute bg-white shadow-lg rounded-4 p-3 mega-menu-fade-in"
                  style={{
                    top: "70px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "300px",
                    cursor: "default",
                    border: "1px solid #f0f0f0",
                    zIndex: 1050,
                  }}
                >
                  <h6
                    className="fw-bold mb-3 small letter-spacing-1 text-uppercase"
                    style={{ color: "#97A3B7" }}
                  >
                    Services
                  </h6>
                  <div className="d-flex flex-column gap-2">
                    {servicesData.map((service) => (
                      <a
                        key={service.slug}
                        href={`/services/${service.slug}`}
                        className="d-flex align-items-center gap-2 text-decoration-none text-dark hover-bg-light p-2 rounded-3 transition-all"
                        title={`View ${service.title}`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            color: "#053b84",
                          }}
                        >
                          {service.icon}
                        </div>
                        <div
                          className="fw-bold mb-0"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {service.title}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about-us"
              className="text-decoration-none text-dark fw-bold small text-nowrap link-hover header-nav-link"
            >
              About Us
            </Link>
            <Link
              to="/contact-us"
              className="text-decoration-none text-dark fw-bold small text-nowrap link-hover header-nav-link"
            >
              Contact Us
            </Link>
          </nav>

          <div className="d-flex align-items-center gap-2 gap-lg-3 header-action-group">
            <a
              href="https://www.researchwire.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-blue-gradient btn-rw-gradient px-3 py-2 fw-bold text-nowrap"
              style={{ fontSize: "0.85rem" }}
            >
              Explore Researchwire
            </a>

            {/* Auth Buttons / User Profile */}
            {endUser ? (
              <div className="d-flex align-items-center gap-2 ms-1">
                <Link
                  to={
                    endUser.hasPassword
                      ? "/workspace"
                      : "/workspace?tab=settings"
                  }
                  className="btn btn-blue-gradient btn-rw-gradient px-3 py-2 fw-bold text-white d-inline-flex align-items-center gap-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  <FaThLarge size={14} /> My Workspace
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn btn-outline-secondary btn-logout-circle rounded-circle p-2 d-flex align-items-center justify-content-center"
                  title="Sign Out"
                  aria-label="Sign Out"
                >
                  <FaSignOutAlt size={14} />
                </button>
              </div>
            ) : (
              <Link
                to="/signin"
                className="btn btn-blue-gradient btn-rw-gradient px-4 py-2 fw-bold text-white"
                style={{ fontSize: "0.85rem" }}
              >
                Sign In
              </Link>
            )}

            {/* Search Icon Toggle */}
            <div className="position-relative ms-1">
              <button
                className="btn btn-link text-dark p-0 border-0"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                style={{ outline: "none", boxShadow: "none" }}
                aria-label="Toggle Search"
              >
                <FaSearch
                  size={18}
                  className="text-dark hover-text-primary transition"
                  style={{ opacity: 0.85 }}
                />
              </button>

              {/* Search Dropdown */}
              {isSearchOpen && (
                <div
                  className="position-absolute bg-white shadow-lg rounded-3 p-3"
                  style={{
                    top: "100%",
                    right: 0,
                    marginTop: "15px",
                    width: "300px",
                    border: "1px solid #f0f0f0",
                    zIndex: 1050,
                  }}
                >
                  <SearchBar
                    enableSuggestions={true}
                    placeholder="Search reports..."
                    onSearch={(query) => {
                      setIsSearchOpen(false);
                      if (query.trim()) {
                        const trimmed = query.trim();
                        if (endUser && saveSearchReport) {
                          saveSearchReport(trimmed, {
                            title: trimmed,
                          });
                        }
                        navigate(
                          `/reports?search=${encodeURIComponent(trimmed)}`,
                        );
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="d-md-none d-flex align-items-center">
          <button
            className="btn btn-link text-dark p-0 text-decoration-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="position-absolute start-0 w-100 bg-white border-top border-bottom shadow-lg p-4 d-flex flex-column gap-2 d-md-none"
          style={{
            top: "72px",
            maxHeight: "calc(100vh - 72px)",
            overflowY: "auto",
            zIndex: 1045,
          }}
        >
          <a
            href="/reports"
            className="text-decoration-none text-dark fw-bold"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            All Reports
          </a>

          {/* Services Mobile Toggle */}
          <div className="d-flex flex-column gap-2">
            <div
              className="d-flex justify-content-between align-items-center fw-bold"
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              style={{ cursor: "pointer" }}
            >
              Services{" "}
              <FaChevronDown
                size={14}
                className={`transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
              />
            </div>
            {isServicesOpen && (
              <div className="d-flex flex-column gap-2 ps-3 mt-2 border-start ms-2">
                {servicesData.map((service) => (
                  <a
                    key={service.slug}
                    href={`/services/${service.slug}`}
                    className="text-decoration-none text-muted small"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {service.title}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Industries Mobile Toggle */}
          <div className="d-flex flex-column gap-2">
            <div
              className="d-flex justify-content-between align-items-center fw-bold"
              onClick={() => setIsIndustriesOpen(!isIndustriesOpen)}
              style={{ cursor: "pointer" }}
            >
              Industries{" "}
              <FaChevronDown
                size={14}
                className={`transition-transform ${isIndustriesOpen ? "rotate-180" : ""}`}
              />
            </div>
            {isIndustriesOpen && (
              <div className="d-flex flex-column gap-2 ps-3 mt-2 border-start ms-2">
                {dynamicHierarchy.map((cat) => (
                  <a
                    key={cat.id}
                    href={`/industries/${slugifyIndustry(cat.name)}`}
                    className="text-decoration-none text-muted small"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/about-us"
            className="text-decoration-none text-dark fw-bold"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About Us
          </Link>
          {/* <div className="d-flex align-items-center gap-1 mt-2 text-muted fw-bold">
                        <FaGlobe /> <span className="ms-1">EN</span> <FaChevronDown size={10} className="ms-1" />
                    </div> */}

          <hr className="text-muted my-2" />

          {endUser ? (
            <div className="d-flex gap-2 mb-2">
              <Link
                to={
                  endUser.hasPassword
                    ? "/workspace"
                    : "/workspace?tab=settings"
                }
                className="btn btn-blue-gradient rounded-pill py-2 fw-bold text-center flex-fill text-white d-flex align-items-center justify-content-center gap-2"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ fontSize: "0.85rem" }}
              >
                <FaThLarge size={13} /> My Workspace
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                className="btn btn-outline-secondary btn-logout-circle rounded-circle p-2 d-flex align-items-center justify-content-center flex-shrink-0"
                title="Sign Out"
                aria-label="Sign Out"
              >
                <FaSignOutAlt size={14} />
              </button>
            </div>
          ) : (
            <div className="d-flex mb-2">
              <Link
                to="/signin"
                className="btn btn-blue-gradient rounded-pill py-2 fw-bold text-center w-100 text-white"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ fontSize: "0.85rem" }}
              >
                Sign In
              </Link>
            </div>
          )}

          <div className="d-flex gap-2">
            <a
              href="https://www.researchwire.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-blue-gradient rounded-pill py-2 fw-bold text-center flex-fill"
              style={{ fontSize: "0.85rem" }}
            >
              Explore Researchwire
            </a>
            <Link
              to="/contact-us"
              className="btn btn-blue-gradient rounded-pill py-2 fw-bold text-center flex-fill"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{ fontSize: "0.85rem" }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
